import type { JSX, KeyboardEvent } from 'react';
import { useLayoutEffect, useRef } from 'react';
import type { ComposerFile } from './use-attachments';
import type { VoiceState } from './use-voice';
import { AttachmentChips, composerHint } from './composer-parts';
import { ComposerActions } from './composer-actions';

export const MAX_QUESTION = 2000;

/**
 * The question box: attachment chips above, an auto-growing textarea (to
 * 140px), and the attach, voice and send buttons. Enter sends; Shift+Enter is
 * a new line. Read-only — someone else's conversation — it says so instead.
 */
export function Composer({
  draft,
  onDraft,
  onSend,
  canSend,
  busy,
  files,
  onPick,
  onRemoveFile,
  voice,
  readOnlyOwner,
}: {
  draft: string;
  onDraft: (text: string) => void;
  onSend: () => void;
  canSend: boolean;
  busy: boolean;
  files: readonly ComposerFile[];
  onPick: (files: File[]) => void;
  onRemoveFile: (key: string) => void;
  voice: { supported: boolean; state: VoiceState; toggle: () => void };
  readOnlyOwner?: string | undefined;
}): JSX.Element {
  const box = useRef<HTMLTextAreaElement | null>(null);
  const listening = voice.state === 'listening';

  useLayoutEffect(() => {
    const el = box.current;
    if (el === null) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [draft]);

  if (readOnlyOwner !== undefined) {
    return (
      <div className="flex-none px-6 pt-3 pb-[18px]">
        <div className="border-line bg-bg text-mute rounded-2xl border px-4 py-3.5 text-[13px]">
          Read-only — this conversation belongs to {readOnlyOwner}.
        </div>
      </div>
    );
  }

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (canSend) onSend();
    }
  };
  const border = listening ? 'var(--bad)' : draft !== '' ? 'var(--acc)' : 'var(--line)';
  const hint = composerHint(voice.state, busy);

  return (
    <div className="flex-none px-6 pt-3 pb-[18px]">
      <AttachmentChips files={files} onRemove={onRemoveFile} />
      <div
        className="bg-bg grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2.5 rounded-2xl border py-2.5 pr-2.5 pl-4 transition-colors"
        style={{
          borderColor: border,
          boxShadow: draft !== '' ? '0 0 0 3px var(--accSoft)' : 'none',
        }}
      >
        <textarea
          ref={box}
          rows={1}
          value={draft}
          maxLength={MAX_QUESTION}
          onChange={(e) => onDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder={listening ? 'Listening…' : 'Ask about a load, truck, driver or broker…'}
          aria-label="Your question"
          className="placeholder:text-mute max-h-[140px] min-h-[30px] resize-none border-0 bg-transparent py-1.5 text-sm leading-normal outline-none focus:outline-none"
        />
        <ComposerActions voice={voice} canSend={canSend} onSend={onSend} onPick={onPick} />
      </div>
      <div className="text-mute mt-2 flex justify-between px-1 font-mono text-[10.5px]">
        <span>{hint}</span>
        <span>Enter to send · Shift+Enter for newline</span>
      </div>
    </div>
  );
}
